import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import { NAV, PHONE, PHONE_TEL } from "../lib/data";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/70 backdrop-blur-2xl border-b border-white/5 py-3" : "bg-transparent py-5"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link"><Logo className="h-12 md:h-16" /></Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} data-scroll={n.href} data-testid={`nav-${n.label}`}
              className="text-sm font-semibold text-white/70 hover:text-volt transition-colors duration-300 relative group">
              {n.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-volt transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href={`tel:${PHONE_TEL}`} data-testid="header-phone-btn"
            className="hidden sm:flex items-center gap-2.5 rounded-full bg-volt text-black font-bold text-sm px-5 py-3 hover:scale-105 transition-transform duration-300 shadow-[0_0_24px_rgba(245,133,26,0.28)]">
            <Phone className="h-4 w-4" strokeWidth={2.5} /> {PHONE}
          </a>
          <button className="lg:hidden text-white p-2" onClick={() => setOpen(!open)} data-testid="menu-toggle" aria-label="Menü">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-black/90 backdrop-blur-2xl border-t border-white/5 mt-3">
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <a key={n.href} href={n.href} data-scroll={n.href} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.label}`}
                  className="text-lg font-semibold text-white/80 hover:text-volt transition-colors">{n.label}</a>
              ))}
              <a href={`tel:${PHONE_TEL}`} className="mt-2 flex items-center gap-2 rounded-full bg-volt text-black font-bold px-5 py-3 w-fit">
                <Phone className="h-4 w-4" /> {PHONE}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
